import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CompanyType, getLiveLanguages, MailTestData } from "../models/company";
import { queryClient } from "../utils/http/queryClient";
import { getTomorrowAt9, getTomorrowNlFormatted } from "../utils/testDates";
import type { AppDispatch, RootState } from "./index";

const initialTestFields = `{
  "EMAILS_TO": ["test@colibry.ai"],
  "EMAILS_BCC": [""],

  "DATE": "${getTomorrowAt9()}",
  "BZ_DATUM": "${getTomorrowNlFormatted()}",
  "BZ_TIJDSTIP_AFSPRAAK": "15:30",
  "FIRSTNAME": "Vik",
  "LASTNAME": "Visitor",
  "AMOUNT_OF_MATCHES": 1,
  "WEBSITE_URL": "https://colibry.ai",

  "FROM_NAME": "Responsible Rep",
  "EMAIL_FROM": "wendy@colibry.ai",

  "PA_ID": "TEST_ID",
  "PA_DOSSIERVERANTWD": "Responsible Rep",
  "PA_IMAGE": "https://storage.googleapis.com/production-co-libry-sp-js/a8ufNDWE/imaginary_forsale.jpg",
  "PA_LINK": "http://some-link-to-property",
  "PA_ADRES": "Pandadres 1, 9000, Gent",
  "PA_ADRES_ALWAYS_PROPERTY": "Pandadres 1, 9000, Gent",
  "PA_TYPE": "het appartement",
  "PA_GOOGLE_MAPS": "https://www.google.com/maps?q=Kompasplein+19/1A+Gent",

  "RA_IMAGE": "https://storage.googleapis.com/automation-cdn/colibry/rexy-realtor-signature-example-me.jpg",
  "RA_FIRSTNAME": "Wendy",
  "RA_LASTNAME": "Geeraert",
  "RA_EMAIL": "wendy@colibry.ai",
  "RA_FUNCTION": "Colibry Founder as Rep.",
  "RA_MOBILE": "0477/77.77.77",
  "RA_BIV": "BIV number",

  "OI_IMAGE": "https://storage.googleapis.com/production-co-libry-sp-js/a8ufNDWE/imaginary_office.jpg",
  "OI_NAME": "9000 Gent",
  "OI_ADDRESS": "Office Address 1",
  "OI_PHONE": "09/123.44.55",

  "AMOUNT_OF_TASKS": 1,

  "HIGHLIGHT_COLOR": "#182b49"
}`;

export const staticTestFields: Record<string, unknown> = JSON.parse(initialTestFields);

// Where the test mail goes and who it comes from: settings for this sitting rather than
// data belonging to a template, so these survive moving between templates (a page reload
// puts them back to the values above).
export const STICKY_TEST_FIELDS = ["EMAILS_TO", "EMAILS_BCC", "FROM_NAME", "EMAIL_FROM"];

const initialStickyFields: Record<string, unknown> = Object.fromEntries(
  STICKY_TEST_FIELDS.filter((key) => key in staticTestFields).map((key) => [key, staticTestFields[key]]),
);

interface CompanyState {
  activeCompany: CompanyType | null;
  blocksAndTemplates: any;
  templateSummary: {
    maintained: any[];
    config: any[];
  };
  mailTemplateTestData: MailTestData; // Should be DB stored in the future
  mailTemplateChangeHistoryInclDrafts: boolean;
}

const initialState: CompanyState = {
  activeCompany: null,
  blocksAndTemplates: {},
  templateSummary: {
    maintained: [],
    config: [],
  },
  mailTemplateTestData: {
    sendLive: false,
    sendDraft: true,
    sendActive: false,
    editedFields: null,
    stickyFields: initialStickyFields,
    datasetName: null,
    languages: ["nl"],
  },
  mailTemplateChangeHistoryInclDrafts: false,
};

const companySlice = createSlice({
  name: "company",
  initialState: { ...initialState },
  reducers: {
    _setActiveCompanyInternal(state, action: PayloadAction<CompanyType | null>) {
      const activeCompany = action.payload;
      if (!activeCompany) {
        localStorage.removeItem("ac");
        state.activeCompany = null;
        return;
      }
      state.activeCompany = { ...activeCompany };
      localStorage.setItem("ac", activeCompany?.id || "");
      queryClient.invalidateQueries({ queryKey: ["mailTemplates"] });

      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "sends"], refetchType: "all" });
      queryClient.resetQueries({ queryKey: ["mailTemplates", "sends"], exact: true }); // Make sure to explicitly clear the cache
    },
    setBlocksAndTemplates(state, action: PayloadAction<any[]>) {
      state.blocksAndTemplates = action.payload;
    },
    setTemplateSummary(state, action: PayloadAction<any>) {
      state.templateSummary = action.payload;
    },
    setMailTemplateTestData(state, action: PayloadAction<any>) {
      state.mailTemplateTestData = action.payload;
    },
    setEditedTestFields(state, action: PayloadAction<{ key: string; text: string } | null>) {
      state.mailTemplateTestData.editedFields = action.payload;
    },
    setStickyTestFields(state, action: PayloadAction<Record<string, unknown>>) {
      state.mailTemplateTestData.stickyFields = action.payload;
    },
    setTestDatasetName(state, action: PayloadAction<string | null>) {
      state.mailTemplateTestData.datasetName = action.payload;
    },
    toggleMailTemplateChangeHistoryInclDrafts(state) {
      state.mailTemplateChangeHistoryInclDrafts = !state.mailTemplateChangeHistoryInclDrafts;
    },
  },
});

// Thunk to set active company and update related state
export const setActiveCompany = (company: CompanyType | null) => (dispatch: AppDispatch, getState: () => RootState) => {
  // Update company state
  dispatch(companySlice.actions._setActiveCompanyInternal(company));

  // Update company slice languages if company has languages
  const liveLanguages = getLiveLanguages(company?.info?.languages);
  if (liveLanguages.length) {
    const currentTestData = getState().company.mailTemplateTestData;
    dispatch(
      companyActions.setMailTemplateTestData({
        ...currentTestData,
        languages: liveLanguages,
      }),
    );
  }
};

export const companyActions = {
  ...companySlice.actions,
  setActiveCompany,
};

export default companySlice;
